import React from 'react';
import { Button, Intent } from '@blueprintjs/core';
import { connect, ConnectedProps } from 'react-redux';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { RootState } from 'data/root.reducers';
import { CustomizationDerivedTargetMap } from 'data/customization/customization.types';
import { CaseModel } from 'data/case/case.types';
import { Warrant } from 'data/warrant/warrant.types';
import { DEPLOYMENT_TUNISIA, QUERY_LOC_FROM_AMF_ID } from 'xcipio/constants';
import * as globalSelectors from 'global/global-selectors';
import { splitCombinedTarget } from 'xcipio/devices/forms/combineTargetsWithSameTypeAndValue';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as warrantSelectors from 'data/warrant/warrant.selectors';
import * as caseActions from 'xcipio/cases/case-actions';
import * as caseSelectors from 'data/case/case.selectors';
import * as targetSelectors from 'data/target/target.selectors';
import {
  TargetModel, TargetModelAfCCC, TargetModelAfCCCSettings, TargetModelMap
} from 'data/target/target.types';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as targetTemplateActions from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import * as targetTemplateSelectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import WarrantConfiguration from 'xcipio/config/warrant-config';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import i18n from 'i18n';
import { TargetDeliveryTemplateAccessFunction } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import * as _ from 'lodash';
import { isTargetInfoSupported } from 'xcipio/cases/advancedProvisioning/accessFunctions';
import { shouldShowTarget } from 'xcipio/cases/advancedProvisioning/targetSummaryOfTargetPanel';
import { adaptToSubmit, getTargetTemplateValues } from 'xcipio/cases/targetInfo/EricssonNETargetInfo/utils';
import { adaptToSubmitForUTM } from 'xcipio/cases/targetInfo/UtimacoLIMSTargetCCC/utils'
import { AccessFunction, AdditionalMO } from 'data/accessFunction/accessFunction.types';
import * as locationSelectors from 'data/location/location.selectors';
import DeviceDetail from './forms/device-detail';
import { FormPanel } from '../../shared/wizard/with-discovery-wizard-step-panel';
import DeviceModeler from './deviceModeler';
import getAllCasesFromDevicesModel from './forms/devices-to-cases';
import WarrantPanel from '../warrants/forms/warrant-panel';
import { DeviceModel, DeviceModelMap, DeviceModelType } from './device.types';
import DevicesFormList from './forms/DevicesFormList';
import { mergeTargets } from './utils/mergeTargets';
import CaseModeler from './forms/case-modeler';
import processCasesChanges from './utils/processCasesChanges';
import { updateCaseIdTarget } from './updateTarget';
import './device-panel.scss';

const deviceModel: DeviceModel = {
  device: '',
  caseId: '',
  services: [],
  types: {},
  allTargetAfs: [],
};

const mapState = (state: RootState) => ({
  warrantConfigInstance: warrantSelectors.getWarrantConfigurationInstance(state),
  changeTracker: warrantSelectors.getChangeTrackerInstance(state),
  derivedTargets: warrantSelectors.getCustomizationDerivedTargets(state),
  targetDefaultAFSelection: warrantSelectors.getTargetDefaultAFSelection(state),
  warrant: warrantSelectors.getWarrant(state),
  cases: caseSelectors.getCases(state),
  targetsById: targetSelectors.getTargetsById(state),
  selectedDF: globalSelectors.getSelectedDeliveryFunctionId(state),
  cfGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  templateUsed: warrantSelectors.getTemplateUsed(state),
  cfs: collectionFunctionSelectors.getCollectionFunctionList(state),
  locations: locationSelectors.getLocationList(state),
  bSelectAllHI3Interfaces: discoverySelectors.shouldSelectAllHi3Interfaces(state),
  shouldAutomaticallyGenerateCaseName: discoverySelectors.shouldAutomaticallyGenerateCaseName(state),
  isTargetIdValidationEnabled: discoverySelectors.isTargetIdValidationEnabled(state),
  isExtendedTargetValidationEnabled: discoverySelectors.isExtendedTargetValidationEnabled(state),
  availableAccessFunction: supportedFeaturesSelectors.getAvailableAccessFunction(state),
  deployment: discoverySelectors.getDiscoveryDeployment(state),
  targetDeliveryTemplates: targetTemplateSelectors.getTargetDeliveryTemplateAccessFunctionList(state),
});

const mapDispatch = {
  updateCasesState: caseActions.updateCasesState,
  setWarrantTracker: warrantActions.setWarrantTracker,
  setWarrantDeviceChanged: warrantActions.setWarrantDeviceChanged
};

const connector = connect(mapState, mapDispatch, null, { forwardRef: true });

type PropsFromRedux = ConnectedProps<typeof connector>;

export type DevicePanelProps = PropsFromRedux & {
  mode: string
  onDevicesReady?: () => void
  onAddDevice?: () => void
  onDeleteDevice?: () => void
};

export type DevicePanelState = {
  devices: DeviceModelMap,
}

export class DevicePanel extends React.Component<DevicePanelProps, DevicePanelState> implements FormPanel {
  private deviceRefs: { [deviceId: string]: DeviceDetail | null } = {}
  private maxId = 0;
  private originalDevices: DeviceModelMap | null = null;
  private hasDeviceChanged = false;

  constructor(props: DevicePanelProps) {
    super(props);

    const devices = this.initializeDevice();

    this.state = {
      devices,
    };
  }

  async componentDidMount(): Promise<void> {
    const { deployment, selectedDF } = this.props
  }

  async componentDidUpdate(prevProps: DevicePanelProps): Promise<void> {
    const {
      warrant,
      cfs,
      mode,
      onDevicesReady,
    } = this.props;

    if (mode !== CONFIG_FORM_MODE.View && DevicePanel.shouldInitializeDevices(warrant, prevProps.warrant)) {
      const devices = this.initializeDevice();

      if (this.shouldAutoSelectSingleOption()) {
        this.autoSelectSingleOption(devices);
        await this.updateDeviceMapTargets(devices);

      } else if (mode === CONFIG_FORM_MODE.Edit) {
        await this.updateDeviceMapTargets(devices);
      }

      this.setState({
        devices,
      });
    }

    if (!isEqual(cfs, prevProps.cfs)) {
      const { devices } = this.state;
      const clonedDevices = cloneDeep(devices);

      await this.updateDeviceMapTargets(clonedDevices);

      onDevicesReady && onDevicesReady();

      this.setState({
        devices: clonedDevices,
      });

      this.originalDevices = cloneDeep(clonedDevices);
    }
  }

  static shouldInitializeDevices(warrant: Warrant | null, prevWarrant: Warrant | null): boolean {
    if (warrant == null) {
      // The warrant is not defined yet, no need to initialize
      return false;
    }

    if (!DevicePanel.isReady(warrant) && DevicePanel.isReady(prevWarrant)) {
      // The Lea was removed, so devices should be initialize
      return true;
    }

    if (warrant.lea !== prevWarrant?.lea) {
      // The Lea was changed, so devices should be initialize
      return true;
    }

    return false;
  }

  initializeDevice(): DeviceModelMap {
    const {
      cases,
      warrant,
      derivedTargets,
      targetsById,
    } = this.props;

    this.maxId = DevicePanel.getMaxDeviceId(cases);

    // Targets with the same primaryType and value are combined into a target when
    // converting devices to cases.
    // Convert the combined target back into targets with the same  primaryType and value
    // when creating devices from cases.
    splitCombinedTarget(cases);

    let devices = DevicePanel.getDevices(warrant, cases, derivedTargets, targetsById);

    if (Object.keys(devices).length === 0) {
      const newDevice = this.createDevice();
      devices = {
        [newDevice.device]: newDevice,
      };
    }

    this.originalDevices = cloneDeep(devices);

    return devices;
  }

  shouldAutoSelectSingleOption(): boolean {
    const { warrantConfigInstance, warrant } = this.props;
    const availableServiceOptions = warrantConfigInstance?.getServiceDropDownOptions() ?? [];

    return availableServiceOptions.length === 1 && DevicePanel.isReady(warrant);
  }

  autoSelectSingleOption(devices: DeviceModelMap): void {
    const { warrantConfigInstance } = this.props;
    const availableServiceOptions = warrantConfigInstance?.getServiceDropDownOptions() ?? [];

    Object.values(devices).forEach((device) => {
      device.services = [availableServiceOptions[0].value];
    });
  }

  static getMaxDeviceId(cases: Partial<CaseModel>[]): number {
    let max = 0;
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < cases.length; i++) {
      const deviceIdString = cases[i].device;
      if (deviceIdString) {
        const deviceId = parseInt(deviceIdString, 10);
        if (deviceId > max) {
          max = deviceId;
        }
      }
    }
    return max;
  }

  static getDevices(
    warrant: Warrant | null,
    cases: CaseModel[],
    derivedTargets: CustomizationDerivedTargetMap,
    targetsById: TargetModelMap,
  ): DeviceModelMap {
    if (warrant == null) {
      return {};
    }

    const model = new DeviceModeler(derivedTargets);
    model.setWarrantData(cloneDeep(warrant));
    cases.forEach((wc) => {
      const warrantCase = cloneDeep(wc);
      model.setCaseData(warrantCase);
      warrantCase.targets.forEach((target) => {
        if (target.caseId != null) {
          model.setTargetData(target);
        } else {
          model.setTargetData(targetsById[target.id as string]);
        }
      });
    });

    return model.buildModel();
  }

  static getHeadersFromDeviceTargets(devices: DeviceModelMap): string[] {
    const headers = new Set<string>();

    Object.keys(devices).forEach((id) => {
      Object.keys(devices[id].types).forEach((type) => {
        const ets = devices[id].types[type];
        const display = ets.some((et) => {
          return et.display;
        });

        if (display) {
          headers.add(type);
        }
      });
    });

    const headerArray = [...headers];
    headerArray.unshift(i18n.t('warrant.form.services'));
    headerArray.unshift(i18n.t('warrant.form.device'));
    headerArray.unshift('');

    return headerArray;
  }

  static isReady(warrant: Warrant | null): boolean {
    return warrant?.lea != null && warrant?.lea !== '';
  }

  createDevice(): DeviceModel {
    const newDevice = cloneDeep(deviceModel);

    this.maxId += 1;
    newDevice.device = this.maxId.toString();

    return newDevice;
  }

  deleteDevice = (deviceId: string): void => {
    const { devices } = this.state;
    const { onDeleteDevice } = this.props;

    const clonedDevices = cloneDeep(devices);
    delete clonedDevices[deviceId];
    delete this.deviceRefs[deviceId];

    this.setState({
      devices: clonedDevices,
    });

    this.setWarrantDeviceChanged();

    if (onDeleteDevice != null) {
      onDeleteDevice();
    }
  }

  addDevice = async (): Promise<void> => {
    const { devices } = this.state;
    const { onAddDevice, warrantConfigInstance } = this.props;

    const newDevice = this.createDevice();

    const availableServiceOptions = warrantConfigInstance?.getServiceDropDownOptions() ?? [];
    if (availableServiceOptions.length === 1) {
      newDevice.services = [availableServiceOptions[0].value];

      await this.updateDeviceTargets(newDevice);
    }

    const clonedDevices = cloneDeep(devices);
    clonedDevices[newDevice.device] = newDevice;

    this.setState({
      devices: clonedDevices,
    });

    this.setWarrantDeviceChanged();
    if (onAddDevice != null) {
      onAddDevice();
    }
  }

  isValid(errors: string[]): boolean {
    let areDevicesValid = true;

    Object.keys(this.deviceRefs).forEach((key) => {
      if (!this.deviceRefs[key]) {
        delete this.deviceRefs[key];
      } else {
        const deviceValid = this.deviceRefs[key]?.isValid() ?? false;
        areDevicesValid = areDevicesValid && deviceValid;
      }
    });

    if (areDevicesValid) {
      const { devices } = this.state;

      const hasAtLeastOneTargetDefinedForEachDevice = DevicePanel.hasAtLeastOneTargetDefinedForEachDevice(
        devices,
        errors,
      );

      areDevicesValid = areDevicesValid && hasAtLeastOneTargetDefinedForEachDevice;
    }

    return areDevicesValid;
  }

  async submit(force = false): Promise<boolean> {

    if (!force && !this.hasAnyChange()) {
      return true;
    }

    const {
      warrantConfigInstance,
      warrant,
      derivedTargets,
      targetDefaultAFSelection,
      cfs,
      locations,
      templateUsed,
      selectedDF,
      bSelectAllHI3Interfaces,
      cases,
      updateCasesState,
      mode,
      targetsById,
      changeTracker,
      setWarrantTracker,
      deployment,
      shouldAutomaticallyGenerateCaseName,
      targetDeliveryTemplates
    } = this.props;

    const {
      devices,
    } = this.state;

    if (warrantConfigInstance == null) {
      return false;
    }

    const ontology = warrantConfigInstance.configuration.config;
    const serviceMapping = warrantConfigInstance.afcfByServiceMap;

    const newCases = await getAllCasesFromDevicesModel(
      warrant,
      serviceMapping,
      derivedTargets,
      targetDefaultAFSelection,
      devices,
      cfs,
      locations,
      templateUsed,
      selectedDF,
      bSelectAllHI3Interfaces ?? false,
      cases,
      targetsById,
      ontology,
      shouldAutomaticallyGenerateCaseName,
    );
    this.updateCasesForTunisia(mode, newCases, deployment, targetDeliveryTemplates);
    updateCasesState(newCases);
    if (mode === CONFIG_FORM_MODE.Edit) {
      processCasesChanges(changeTracker, cases, newCases, targetsById);
      setWarrantTracker(changeTracker);
    }
    return true;
  }

  static hasAtLeastOneTargetDefinedForEachDevice(devices: DeviceModelMap, errors: string[]): boolean {
    if (Object.values(devices).length === 0) {
      return true;
    }

    return Object.values(devices).every((device) => {
      const hasAtLeastOneTargetDefined = DevicePanel.hasAtLeastOneTargetDefined(device);

      if (!hasAtLeastOneTargetDefined) {
        errors.push(i18n.t('warrant.form.error.missingInformationDevice', { device: device.device }));
      }

      return hasAtLeastOneTargetDefined;
    });
  }

  static hasAtLeastOneTargetDefined({ services, types }: DeviceModel): boolean {
    if (services.length === 0) {
      return true;
    }

    return Object.values(types).some((targetType) => {
      return targetType.some(({ target }) => !!target.primaryValue);
    });
  }

  hasAnyChange(): boolean {
    const {
      devices,
    } = this.state;

    return !isEqual(devices, this.originalDevices);
  }

  updateCasesForTunisia(mode: string, newCases: CaseModel[], deployment: string, targetDeliveryTemplates: TargetDeliveryTemplateAccessFunction[]): void {
    if (mode !== CONFIG_FORM_MODE.Create) {
      return;
    }

    newCases.forEach((c) => {
      const neAfs = c.afMapping?.ERICSSON_NE ?? [];
      const afIdToNeTypeMap = this.buildAfIdToNeTypeMap(neAfs);
      c.targets.filter((t) => shouldShowTarget(t, true)).forEach((t) => {
        if (_.isEmpty(t.afs?.afccc)) {
          const allAfs = (t.afs && t.afs.afIds) ? t.afs.afIds.filter((af: any) => (af.bAFAssociation)) : [];
          allAfs.filter((af) => isTargetInfoSupported(af, t.primaryType as string)).forEach((af) => {
            if (af.tag === 'ERICSSON_NE') {
              const neTemplates = targetDeliveryTemplates.filter((temp) => temp.afType === af.tag);
              const selectedTemplate = neTemplates.find((t) => {
                const generalSection = (t.templateSections ?? []).find((section) => section.name === 'EIMSGENERAL');
                let options: { key:string, value: string | boolean | undefined }[] = [];
                if (generalSection) {
                  options = generalSection.options ?? [];
                }
                const neTypeOption = options.find((o) => o.key === 'netype');
                return afIdToNeTypeMap[af.id as string] === (neTypeOption?.value ?? '');
              });
              if (selectedTemplate) {
                const targetTemplateValues = ['EIMSTARGET', 'EIMSMSCTRGT', 'EIMSGPRSTRGT', 'EIMSMCNB', 'EIMSMC'].reduce((acc, sectionKey: string) => {
                  if (!acc[sectionKey]) {
                    acc[sectionKey] = getTargetTemplateValues(sectionKey, selectedTemplate as TargetDeliveryTemplateAccessFunction);
                  }
                  return acc;
                }, {} as {[id: string]: any});
                const afSettings = adaptToSubmit(af.id as string, targetTemplateValues, true, undefined);
                afSettings.forEach((afSetting) => {
                  if (afSetting.options && selectedTemplate.id) {
                    afSetting.options = [...afSetting.options, { key: 'targetDeliveryTemplate', value: selectedTemplate.id }];
                  }
                })
                this.addAfCCC(t?.afs?.afccc, afSettings, af.type as string)
              }
            } else if (af.tag === 'SIEMENS_LIMS') {
              const utmTemplates = targetDeliveryTemplates.filter((temp) => {
                return temp.afType === af.tag
              });
              if (!utmTemplates || utmTemplates.length <= 0) {
                return;
              }
              const selectedUtmTemplate = utmTemplates[0]
              const targetUtmTemplateValues = ['targetInformation', 'monitoringCenterFlags', 'ICD', 'networkType', 'callContentType'].reduce((acc, sectionKey: string) => {
                if (!acc[sectionKey]) {
                  acc[sectionKey] = getTargetTemplateValues(sectionKey, selectedUtmTemplate as TargetDeliveryTemplateAccessFunction);
                }
                return acc;
              }, {} as {[id: string]: any});
              const afSettings = adaptToSubmitForUTM(af.id as string, targetUtmTemplateValues, true, selectedUtmTemplate.id);
              this.addAfCCC(t?.afs?.afccc, afSettings, af.type as string)
            }
          });
        }
      });
    });
  }

  addAfCCC(afccc: TargetModelAfCCC[] | undefined, afSettings: TargetModelAfCCCSettings[], afType: string) {
    if (!afccc) {
      return;
    }
    const matchingAfCCC = afccc?.find((af: any) => af.afType === afType)
    if (matchingAfCCC) {
      matchingAfCCC.afSettings = [...matchingAfCCC.afSettings, ...afSettings]
    } else {
      afccc.push({
        afType,
        afSettings
      });
    }
  }

  buildAfIdToNeTypeMap(afs: AccessFunction[]): {[key: string]: string}{
    return afs.reduce((acc, curr: AccessFunction) => {
      const additionalMOs = (!_.isEmpty(curr.additionalMOs) ? (curr.additionalMOs as AdditionalMO[])[0] : null);
      if (additionalMOs) {
        const neType = (additionalMOs.options ?? []).find((o) => o.key === 'netype');
        if (neType) {
          acc[curr.id as string] = neType.value;
        }
      }
      return acc;
    }, {} as any);
  }

  hasConfiguredDevices(): boolean {
    const { devices } = this.state;

    if (Object.keys(devices).length === 0) {
      return false;
    }

    return Object.entries(devices).some(([_deviceId, device]) => device.services.length > 0);
  }

  deviceServiceUpdateHandler = async (deviceId: string, services: string[]): Promise<void> => {
    const { devices } = this.state;

    if (devices[deviceId] == null) {
      return;
    }

    const clonedDevices = cloneDeep(devices);
    const clonedDevice = clonedDevices[deviceId];

    clonedDevice.services = services;

    await this.updateDeviceTargets(clonedDevice);

    this.setState({
      devices: clonedDevices,
    });

    this.setWarrantDeviceChanged();
  }

  deviceTargetUpdateHandler = (
    deviceId: string,
    target: TargetModel,
    targetType: string,
    targetIndex: number,
  ): void => {
    const { devices } = this.state;
    const { warrant } = this.props;

    if (devices[deviceId] == null) {
      return;
    }

    const clonedDevices = cloneDeep(devices);
    const clonedDevice = clonedDevices[deviceId];

    const clonedTarget = cloneDeep(target);

    const deviceTarget = clonedDevice.types[targetType][targetIndex].target;
    deviceTarget.primaryValue = clonedTarget.primaryValue;
    deviceTarget.secondaryValue = clonedTarget.secondaryValue;

    if (targetType === 'VZID') {
      deviceTarget.options = [{ key: 'PCRFUsername', value: clonedTarget.optionValue || '' }];
      deviceTarget.optionValue = clonedTarget.optionValue;
      deviceTarget.secondaryType = clonedTarget.secondaryType;
    } else {
      deviceTarget.options = clonedTarget.options;
    }

    if (warrant?.targetOptions != null
      && warrant?.caseType === 'QUERY_LOC_FROM_AMF'
      && warrant?.caseOptTemplateUsed === QUERY_LOC_FROM_AMF_ID) {

      const currentOptions = deviceTarget.options || [];
      const targetOptionsValue = warrant.targetOptions.LOC_POLL_FREQ || '0';

      deviceTarget.options = [
        ...currentOptions,
        { key: 'LOC_POLL_FREQ', value: targetOptionsValue },
      ];
    }

    this.setState({
      devices: clonedDevices,
    });

    this.setWarrantDeviceChanged();
  }

  async updateDeviceMapTargets(devices: DeviceModelMap): Promise<void> {
    const deviceEntries = Object.values(devices);

    // eslint-disable-next-line no-restricted-syntax
    for (const device of deviceEntries) {
      await this.updateDeviceTargets(device);
    }
  }

  async updateDeviceTargets(device: DeviceModel): Promise<void> {
    const {
      warrantConfigInstance,
      cfs,
      derivedTargets,
      cases,
      availableAccessFunction,
    } = this.props;

    if (warrantConfigInstance == null) {
      return;
    }

    let possibleCollectionFunctions = cfs;

    if (!CaseModeler.isNewDevice(device)) {
      // For existing devices we should filter the CFs by ones used in the cases instead
      // of all available CFs. This is to make sure that the targets to be generated
      // for the device doesn't contain any target from removed cases (CFs)
      const casesForDevice = CaseModeler.getCasesForDevice(device, cases);
      const cfTagsFromCases = casesForDevice
        .map((entry) => {
          const collectionFunctionFound = cfs.find(({ id }) => id === entry.cfId.id);
          return collectionFunctionFound?.tag ?? '';
        })
        .filter((entry) => entry !== '');

      possibleCollectionFunctions = cfs.filter(({ tag }) => cfTagsFromCases.indexOf(tag) !== -1);
    }

    const cfTypes = WarrantPanel.cfsToTypes(possibleCollectionFunctions);

    await warrantConfigInstance.computeSelectedAfTypesForDevices(device.services, cfTypes);

    updateCaseIdTarget(device, warrantConfigInstance);

    let targetTypes: DeviceModelType = {};

    if (WarrantConfiguration.hasUnsupportedServices(device.services)) {
      targetTypes = device.types;

      // For warrants created in XMS, each device will be mapped to a single case
      const [warrantCase] = CaseModeler.getCasesForDevice(device, cases);
      const targets = this.getTargetsForCase(warrantCase);
      if (warrantCase.cfId.tag != null) {
        const cfId = {
          tag: warrantCase.cfId.tag,
        };
        await warrantConfigInstance.computeAfsBasedOnCfAndTargets(cfId, targets, availableAccessFunction);
      }

    } else {
      warrantConfigInstance.computePossibleTargets(device.services, derivedTargets, availableAccessFunction);

      // get derivedTargets based upon possible targets and customization config in redux
      const possibleTypes = warrantConfigInstance.getDerivedTypes(device.services, derivedTargets);

      // Existing target that are not in the possible targets should be
      // merged only for existing devices. New devices should follow what
      // is defined in the ontology.
      const shouldMergeExistingTargets = !CaseModeler.isNewDevice(device);

      // merge the targets that are supported by the selected services
      // and the targets already assigned to the device.
      targetTypes = mergeTargets(possibleTypes, device.types, shouldMergeExistingTargets, device.services);
    }

    device.types = targetTypes;
    device.allTargetAfs = warrantConfigInstance.getTargetWithAllAfs();
    device.afMapping = cloneDeep(warrantConfigInstance.afsByTypeMap);
  }

  getTargetsForCase(caseEntry: CaseModel): TargetModel[] {
    const {
      targetsById,
    } = this.props;

    return caseEntry.targets.map((target) => {
      return targetsById[target.id as string];
    });
  }

  setWarrantDeviceChanged(): void {
    const { setWarrantDeviceChanged } = this.props;

    if (!this.hasDeviceChanged) {
      this.hasDeviceChanged = true;
      setWarrantDeviceChanged(true);
    }
  }

  getDeviceChanged(): boolean {
    return this.hasDeviceChanged;
  }

  renderAddDeviceButton(isReady:boolean): JSX.Element | null{
    const {
      mode,
    } = this.props;
    if (mode === CONFIG_FORM_MODE.View) {
      return null;
    }

    return (
      <Button
        data-testid="addDeviceButton"
        minimal
        disabled={!isReady}
        intent={Intent.PRIMARY}
        icon="add"
        text={i18n.t('warrant.form.addDevice')}
        onClick={this.addDevice}
      />
    );
  }

  render(): JSX.Element {
    this.deviceRefs = {};

    const {
      warrantConfigInstance,
      warrant,
      mode,
      templateUsed,
      isTargetIdValidationEnabled,
      isExtendedTargetValidationEnabled,
    } = this.props;

    const {
      devices,
    } = this.state;

    const headers = DevicePanel.getHeadersFromDeviceTargets(devices);
    const availableServiceOptions = warrantConfigInstance?.getServiceDropDownOptions() ?? [];
    const isReady = DevicePanel.isReady(warrant) || mode === CONFIG_FORM_MODE.Edit;

    return (
      <div className="devicesContainer" data-testid="WarrantDevices">
        <DevicesFormList
          deviceRefs={this.deviceRefs}
          isReady={isReady}
          devices={devices}
          mode={mode}
          headers={headers}
          availableServiceOptions={availableServiceOptions}
          templateUsed={templateUsed}
          warrantConfigInstance={warrantConfigInstance}
          isTargetIdValidationEnabled={isTargetIdValidationEnabled}
          isExtendedTargetValidationEnabled={isExtendedTargetValidationEnabled}
          onDeleteDevice={this.deleteDevice}
          onDeviceServiceUpdate={this.deviceServiceUpdateHandler}
          onDeviceTargetUpdate={this.deviceTargetUpdateHandler}
        />
        {this.renderAddDeviceButton(isReady)}
        <span className="device-panel-note">
          {i18n.t('warrant.form.deviceNote')}
        </span>
      </div>
    );
  }
}

export default connector(DevicePanel);
